/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Cross2Icon } from "@radix-ui/react-icons";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/components/shadcn/dialog";
import { cn } from "@/shared/ui/utils/tailwind";
import {
  preventOpenComboboxEscape,
  preventSelectPortalDismiss,
} from "@/shared/ui/components/form/select-portal-dismiss";

export type ModalProps = {
  opened: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  centered?: boolean;
};

const sizeClasses: Record<string, string> = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
};

/**
 * Modal adapter that delegates to the shadcn Dialog primitive.
 *
 * @param {ModalProps} props - Modal properties.
 * @returns {JSX.Element} Rendered dialog.
 */
export const Modal = ({
  opened,
  onClose,
  title,
  children,
  size = "md",
}: ModalProps) => {
  const { t } = useTranslation("common");

  return (
    <Dialog open={opened} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className={cn("rounded-(--radius-xl)", sizeClasses[size])}
        onPointerDownOutside={preventSelectPortalDismiss}
        onInteractOutside={preventSelectPortalDismiss}
        onEscapeKeyDown={preventOpenComboboxEscape}
      >
        {/* Escape and the overlay already close the dialog, but neither is
            visible: a modal whose only exits are invisible reads as a trap.
            The control sits in the corner whether or not a title was given,
            so every modal built on this adapter has a way out on screen. */}
        <DialogClose
          aria-label={t("a11y.close")}
          className="absolute right-(--space-4) top-(--space-4) flex items-center justify-center rounded-(--radius-md) p-(--space-2) text-(--text-tertiary) bg-transparent border-0 cursor-pointer transition-[var(--transition-standard)] hover:text-(--text-primary) hover:bg-(--surface-tertiary) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--seed-base)"
        >
          <Cross2Icon width={18} height={18} />
        </DialogClose>
        {title && (
          <DialogHeader>
            <DialogTitle className="pe-(--space-8)">{title}</DialogTitle>
          </DialogHeader>
        )}
        <div className="overflow-y-auto max-h-[calc(100vh-8rem)]">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
